import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { fetchDashboardRecordingDetail } from "../../api/dashboardResults";
import { SignalChart } from "../charts/SignalChart";
import PoincareChart from "../charts/PoincareChart";
import { useDateTimeFormat } from "../../hooks/useDateTimeFormat";
import {
  filterPatientRecordingPresentation,
  isDashboardResultFeatureVisible,
} from "../../utils/patientPresentationFeatures";
import { formatValidatedMeasurement } from "../../utils/results/validatedRanges";
import { isMetricSuppressed } from "../../utils/results/processingOutcomes";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../utils/results/spirometry";
import {
  getPointSeriesMaxSecs,
  normaliseTimeValuePoints,
} from "../../utils/results/viewTimeSeries";
import { pickHeartDxHrBpm, pickHeartDxHRV } from "../../utils/results/heartdx";
import {
  pickHrSignalQuality,
  pickRr,
  pickRrSignalQuality,
} from "../../utils/results/signalQuality";
import {
  buildPoincareFromIntervals,
  pickCardiopulmonaryDetrendedSignal,
  pickHeartDxPeakIntervals,
} from "../../utils/results/cardiopulmonaryView";
import { parseTimestampFromRecordingId } from "../../utils/results/recordingIdTime";
import {
  hasMetricValue,
  isWithinPendingWindow,
} from "../../utils/results/metricPending";
import { getEnvironmentalContextSnapshot } from "../../utils/environmentalContextSummary";
import {
  pickPostExertionRrAvg,
  pickPostExertionRrMax,
  pickPostExertionRrMin,
} from "../../utils/results/postExertion";
import { pickAveragedFlowLoop } from "../../utils/results/flowLoops";
import AveragedFlowLoopsSection from "./AveragedFlowLoopsSection";
import FlowVolumeLoopsSection from "./FlowVolumeLoopsSection";
import EnvironmentalContextSection from "./patientRecordingOverlay/EnvironmentalContextSection";
import RecordingViewerSection from "./patientRecordingOverlay/RecordingViewerSection";
import SixMinuteWalkAssessmentSection from "./patientRecordingOverlay/SixMinuteWalkAssessmentSection";
import { pickSixMinuteWalkPairedMeasurementId } from "../../utils/results/sixMinuteWalk";

const recordingDocumentCache = new Map();
const pendingRecordingDocumentLoads = new Map();
const REVIEW_MEDIA_CACHE_REFRESH_BUFFER_MS = 60_000;

function normaliseStatus(value) {
  if (typeof value !== "string") return "unknown";

  const normalized = value.trim().toLowerCase();

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good"
  ) {
    return normalized === "passed" ? "pass" : normalized;
  }

  if (
    normalized === "fail" ||
    normalized === "failed" ||
    normalized === "bad" ||
    normalized === "poor"
  ) {
    return normalized === "failed" ? "fail" : normalized;
  }

  return "unknown";
}

function formatStatusValue(value, t) {
  const normalized = normaliseStatus(value);

  if (normalized === "very good") {
    return t("profilePage.statusVeryGood", "Very good");
  }

  if (normalized === "good") {
    return t("profilePage.statusGood", "Good");
  }

  if (normalized === "pass") {
    return t("profilePage.statusPass", "Pass");
  }

  if (normalized === "fail") {
    return t("profilePage.statusFail", "Fail");
  }

  if (normalized === "bad") {
    return t("profilePage.statusBad", "Bad");
  }

  return t("profilePage.statusUnknown", "Unknown");
}

function formatPresentedMeasurement(data, metric, value, fallback = "--") {
  return isMetricSuppressed(data, metric)
    ? fallback
    : formatValidatedMeasurement(metric, value, fallback);
}

function presentedTrendValue(data, metric, value) {
  return isMetricSuppressed(data, metric) ? null : toTrendNumber(value);
}

function hasResolvedQualityStatus(value) {
  return normaliseStatus(value) !== "unknown";
}

function hasFailedQualityStatus(value) {
  const normalised = normaliseStatus(value);
  return normalised === "fail" || normalised === "bad";
}

function renderCalculatingValue(t) {
  return (
    <span className="animate-pulse text-neutral-400">
      {t("profilePage.metricCalculating", "Calculating…")}
    </span>
  );
}

function classifyRecording(entry, data) {
  const variant = entry?.variant || "";
  const workflowStage =
    typeof data?.workflow?.stage === "string"
      ? data.workflow.stage.trim().toLowerCase()
      : "";

  if (
    variant === "RecordingSpirometry" ||
    data?.results?.mpm?.spirometry ||
    data?.results?.mpm?.flow_loop?.summary_metrics?.spirometryBreathCount
  ) {
    return "spirometry";
  }

  if (variant === "RecordingPostExertion") {
    return "post-exertion";
  }

  if (variant === "RecordingPulseScan" || data?.appMode === "pulse") {
    return "pulse";
  }

  if (variant === "RecordingSixMinuteWalk" || data?.appMode === "six-minute-walk") {
    return workflowStage === "post" || data?.workflow?.walk
      ? "six-minute-walk"
      : "cardiopulmonary";
  }

  if (variant === "RecordingManualAudio") {
    return "manual-audio";
  }

  return "cardiopulmonary";
}

function formatOverlayMetric(metric, fallback = "--") {
  if (typeof metric === "number" && Number.isFinite(metric)) {
    return String(metric);
  }

  if (typeof metric === "string" && metric.trim()) {
    return metric;
  }

  return fallback;
}

function formatFixedOverlayMetric(metric, decimals = 3, fallback = "--") {
  if (typeof metric === "number" && Number.isFinite(metric)) {
    return metric.toFixed(decimals);
  }

  if (typeof metric === "string" && metric.trim()) {
    return metric;
  }

  return fallback;
}

function toTrendNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function buildRecordingDocumentCacheKey(uid, variant, name) {
  return `${uid}::${variant}::${name}`;
}

function isRecordingDocumentCacheEntryFresh(match) {
  const reviewMediaExpiresAt = match?.data?.reviewMedia?.expiresAt;

  if (!reviewMediaExpiresAt) {
    return true;
  }

  const expiresAtMs = Date.parse(reviewMediaExpiresAt);
  if (!Number.isFinite(expiresAtMs)) {
    return false;
  }

  return expiresAtMs - Date.now() > REVIEW_MEDIA_CACHE_REFRESH_BUFFER_MS;
}

async function loadExactRecordingDocument({ uid, variant, name, docId }) {
  const cacheKey = buildRecordingDocumentCacheKey(uid, variant, name);

  if (recordingDocumentCache.has(cacheKey)) {
    const cachedMatch = recordingDocumentCache.get(cacheKey);
    if (isRecordingDocumentCacheEntryFresh(cachedMatch)) {
      return cachedMatch;
    }

    recordingDocumentCache.delete(cacheKey);
  }

  if (pendingRecordingDocumentLoads.has(cacheKey)) {
    return pendingRecordingDocumentLoads.get(cacheKey);
  }

  const pendingLoad = fetchDashboardRecordingDetail({
    uid,
    docId: docId || name,
    variant,
  })
    .then((detail) => {
      if (!detail?.measurement) {
        throw new Error("recording-not-found");
      }

      const match = {
        variant: detail.variant || variant || null,
        data: detail.measurement,
        detail,
      };
      recordingDocumentCache.set(cacheKey, match);

      return match;
    })
    .finally(() => {
      pendingRecordingDocumentLoads.delete(cacheKey);
    });

  pendingRecordingDocumentLoads.set(cacheKey, pendingLoad);
  return pendingLoad;
}

export async function loadPatientRecordingDocument(entry, options = {}) {
  if (!entry?.uid || !entry?.name) {
    throw new Error("recording-not-found");
  }

  return loadExactRecordingDocument({
    uid: entry.uid,
    variant: entry?.collectionName || entry?.variant || options.variant || null,
    name: entry.name,
    docId: entry.docId || entry.name,
  });
}

function buildRelatedRecordingEntry(referenceEntry, docId) {
  if (!referenceEntry?.uid || !docId) {
    return null;
  }

  const variant =
    referenceEntry?.collectionName || referenceEntry?.variant || null;

  return {
    uid: referenceEntry.uid,
    docId,
    name: docId,
    takenAt: referenceEntry?.takenAt || null,
    variant,
    collectionName: variant,
  };
}

function resolveSixMinuteWalkPairedEntry(entry, data = null) {
  const explicitEntry = entry?.sixMinuteWalkAssessment?.preEntry;

  if (explicitEntry?.uid && (explicitEntry?.name || explicitEntry?.docId)) {
    return {
      ...explicitEntry,
      name: explicitEntry.name || explicitEntry.docId,
      docId: explicitEntry.docId || explicitEntry.name,
    };
  }

  const pairedMeasurementId =
    pickSixMinuteWalkPairedMeasurementId(entry) ||
    pickSixMinuteWalkPairedMeasurementId(data);

  return buildRelatedRecordingEntry(entry, pairedMeasurementId);
}

export function buildRecordingPresentation(entry, data, t) {
  const kind = classifyRecording(entry, data);

  if (kind === "six-minute-walk") {
    return {
      kind,
      title: t(
        "profilePage.recordingKinds.sixMinuteWalk",
        "6-Minute Walk Test",
      ),
      metrics: [],
      statuses: [],
      charts: [],
    };
  }

  if (kind === "spirometry") {
    const spirometry = pickSpirometryResult(data, {
      predictionSource: SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
    });
    const spirometryMetricsDisabled =
      hasFailedQualityStatus(spirometry.signalQuality) ||
      hasFailedQualityStatus(spirometry.spirometryQuality) ||
      spirometry.zeroedMetrics === true;
    const fullSignalSeries = [
      {
        label: t("profilePage.rawSignalSeries", "Raw"),
        points: spirometry.processedSignal.signalTracePoints,
        stroke: "#8F8F93",
      },
      {
        label: t("profilePage.detrendedSignalSeries", "Detrended"),
        points: spirometry.processedSignal.detrendedSignalPoints,
        stroke: "#107A92",
      },
    ].filter((seriesItem) => seriesItem.points?.length);

    const spirometrySegmentSeries = [
      {
        label: t("profilePage.rawSignalSeries", "Raw"),
        points: spirometry.processedSignal.spirometrySignalTracePoints,
        stroke: "#B776B9",
      },
      {
        label: t("profilePage.detrendedSignalSeries", "Detrended"),
        points: spirometry.processedSignal.spirometryDetrendedSignalPoints,
        stroke: "#852170",
      },
    ].filter((seriesItem) => seriesItem.points?.length);

    const trendMetrics = [
      {
        key: "meanExhaleInhaleAreaRatio",
        featureKey: "relativeChange",
        label: t(
          "profilePage.meanExhaleInhaleAreaRatio",
          "Mean Exhale/Inhale Area Ratio",
        ),
        value: formatFixedOverlayMetric(
          spirometry.additionalMetrics?.meanExhaleInhaleAreaRatio,
        ),
        rawValue: toTrendNumber(
          spirometry.additionalMetrics?.meanExhaleInhaleAreaRatio,
        ),
        unit: null,
        trendDecimals: 3,
      },
      {
        key: "meanRectangularAreaRatio",
        featureKey: "relativeChange",
        label: t(
          "profilePage.meanRectangularAreaRatio",
          "Mean Rectangular Area Ratio",
        ),
        value: formatFixedOverlayMetric(
          spirometry.additionalMetrics?.meanRectangularAreaRatio,
        ),
        rawValue: toTrendNumber(
          spirometry.additionalMetrics?.meanRectangularAreaRatio,
        ),
        unit: null,
        trendDecimals: 3,
      },
    ];

    return {
      kind,
      title: t("profilePage.recordingKinds.spirometry", "Spirometry"),
      metrics: [
        {
          key: "fev1",
          featureKey: "fev1",
          label: "FEV1",
          value: formatPresentedMeasurement(data, "fev1", spirometry.fev1),
          rawValue: presentedTrendValue(data, "fev1", spirometry.fev1),
          unit: "L",
          trendDecimals: 2,
          disabled: spirometryMetricsDisabled,
        },
        {
          key: "fvc",
          featureKey: "fvc",
          label: "FVC",
          value: formatPresentedMeasurement(data, "fvc", spirometry.fvc),
          rawValue: presentedTrendValue(data, "fvc", spirometry.fvc),
          unit: "L",
          trendDecimals: 2,
          disabled: spirometryMetricsDisabled,
        },
        {
          key: "fev1FvcRatio",
          featureKey: "fev1Fvc",
          label: "FEV1/FVC",
          value: formatPresentedMeasurement(
            data,
            "fev1FvcRatio",
            spirometry.fev1FvcRatio,
          ),
          rawValue: presentedTrendValue(data, "fev1FvcRatio", spirometry.fev1FvcRatio),
          unit: null,
          trendDecimals: 2,
          disabled: spirometryMetricsDisabled,
        },
        {
          key: "pef",
          featureKey: "pef",
          label: "PEF",
          value: formatPresentedMeasurement(data, "pef", spirometry.pef),
          rawValue: presentedTrendValue(data, "pef", spirometry.pef),
          unit: "L/min",
          trendDecimals: 0,
          disabled: spirometryMetricsDisabled,
        },
        {
          key: "rr",
          featureKey: "rr",
          label: "RR",
          value: formatPresentedMeasurement(
            data,
            "rr",
            spirometry.rr?.average ?? spirometry.rr?.mean,
          ),
          rawValue: presentedTrendValue(
            data,
            "rr",
            spirometry.rr?.average ?? spirometry.rr?.mean,
          ),
          unit: "bpm",
          trendDecimals: 0,
          disabled: spirometryMetricsDisabled,
        },
      ],
      statuses: [
        {
          key: "signalQuality",
          featureKey: "signalQuality",
          label: t("profilePage.signalQuality", "Signal quality"),
          value: formatStatusValue(spirometry.signalQuality, t),
          rawValue: spirometry.signalQuality,
          trendType: "quality",
        },
        {
          key: "spirometryQuality",
          featureKey: "spirometryQuality",
          label: t("profilePage.spirometryQuality", "Spirometry quality"),
          value: formatStatusValue(spirometry.spirometryQuality, t),
          rawValue: spirometry.spirometryQuality,
          trendType: "quality",
        },
      ],
      trendMetrics,
      // Render the averaged flow loop inside the charts grid so it sits beside
      // the (half-width) spirometry segment, under the full-width signal trace.
      averagedFlowLoopInline: true,
      charts: [
        {
          type: "time-series",
          featureKey: "respiratorySignalChart",
          // Span the full width so the spirometry segment + averaged flow loop
          // can sit side by side in the row beneath it.
          fullWidth: true,
          title: t("profilePage.fullSignalTraceTitle", "Full signal trace"),
          subtitle: t(
            "profilePage.fullSignalTraceSubtitle",
            "Raw and detrended traces across the full recording.",
          ),
          series: fullSignalSeries,
          emptyLabel: t(
            "profilePage.noSignalTrace",
            "No signal trace is available for this recording.",
          ),
        },
        {
          type: "time-series",
          featureKey: "spirometrySegmentChart",
          // Fills its card so it matches the height of the averaged flow-loop
          // card sitting beside it (that taller sibling sets the row height).
          fill: true,
          title: t("profilePage.spirometrySegmentTitle", "Spirometry segment"),
          subtitle: t(
            "profilePage.spirometrySegmentSubtitle",
            "Raw and detrended traces for the detected spirometry segment.",
          ),
          series: spirometrySegmentSeries,
          emptyLabel: t(
            "profilePage.noSpirometrySegmentSignal",
            "No spirometry segment trace is available for this recording.",
          ),
        },
      ],
    };
  }

  if (kind === "manual-audio") {
    return {
      kind,
      title: t("profilePage.recordingKinds.manualAudio", "Manual audio"),
      metrics: [],
      statuses: [],
      charts: [],
    };
  }

  const rr = pickPostExertionRrAvg(data) ?? pickRr(data);
  const rrMin = pickPostExertionRrMin(data);
  const rrMax = pickPostExertionRrMax(data);
  const hr = pickHeartDxHrBpm(data);
  const hrv = pickHeartDxHRV(data);
  const rrQuality = pickRrSignalQuality(data);
  const hrQuality = pickHrSignalQuality(data);
  const cardiacMetricsDisabled = hasFailedQualityStatus(hrQuality);
  const respiratoryMetricsDisabled = hasFailedQualityStatus(rrQuality);
  const detrendedSignal = pickCardiopulmonaryDetrendedSignal(data);
  const cardiacPoincare = buildPoincareFromIntervals(
    pickHeartDxPeakIntervals(data),
  );
  const isPulseKind = kind === "pulse";
  const isPostExertionKind = kind === "post-exertion";
  const showCardiacPoincare = isPulseKind || kind === "cardiopulmonary";

  return {
    kind,
    title: t(
      isPulseKind
        ? "profilePage.recordingKinds.pulse"
        : "profilePage.recordingKinds.cardiopulmonary",
      isPulseKind ? "Pulse" : "Cardiopulmonary",
    ),
    metrics: [
      {
        key: "hr",
        featureKey: "hr",
        label: "HR",
        value: formatPresentedMeasurement(data, "hr", hr),
        rawValue: presentedTrendValue(data, "hr", hr),
        unit: "bpm",
        trendDecimals: 0,
        disabled: cardiacMetricsDisabled,
      },
      {
        key: "hrv",
        featureKey: "hrv",
        label: "HRV",
        value: formatPresentedMeasurement(data, "hrv", hrv),
        rawValue: presentedTrendValue(data, "hrv", hrv),
        unit: "ms",
        trendDecimals: 0,
        disabled: cardiacMetricsDisabled,
      },
      ...(isPulseKind
        ? []
        : [
            {
              key: "rr",
              featureKey: "rr",
              label: "RR",
              value: formatPresentedMeasurement(data, "rr", rr),
              rawValue: presentedTrendValue(data, "rr", rr),
              unit: "bpm",
              trendDecimals: 0,
              disabled: respiratoryMetricsDisabled,
            },
            ...(isPostExertionKind
              ? [
                  {
                    key: "rrMin",
                    featureKey: "rr",
                    label: t("profilePage.rrMin", "RR Min"),
                    value: formatOverlayMetric(
                      formatValidatedMeasurement("rr", rrMin, "--"),
                    ),
                    rawValue: toTrendNumber(rrMin),
                    unit: "bpm",
                    trendDecimals: 0,
                    disabled: respiratoryMetricsDisabled,
                  },
                  {
                    key: "rrMax",
                    featureKey: "rr",
                    label: t("profilePage.rrMax", "RR Max"),
                    value: formatOverlayMetric(
                      formatValidatedMeasurement("rr", rrMax, "--"),
                    ),
                    rawValue: toTrendNumber(rrMax),
                    unit: "bpm",
                    trendDecimals: 0,
                    disabled: respiratoryMetricsDisabled,
                  },
                ]
              : []),
          ]),
    ],
    statuses: [
      {
        key: "cardiacQuality",
        featureKey: "cardiacQuality",
        label: t("profilePage.cardiacQuality", "Cardiac quality"),
        value: formatStatusValue(hrQuality, t),
        rawValue: hrQuality,
        trendType: "quality",
      },
      ...(isPulseKind
        ? []
        : [
            {
              key: "respiratoryQuality",
              featureKey: "signalQuality",
              label: t("profilePage.respiratoryQuality", "Respiratory quality"),
              value: formatStatusValue(rrQuality, t),
              rawValue: rrQuality,
              trendType: "quality",
            },
          ]),
    ],
    charts: [
      ...(!isPulseKind
        ? [
            {
              type: "time-series",
              featureKey: "respiratorySignalChart",
              title: t(
                "profilePage.respiratorySignalChartTitle",
                "Respiratory signal",
              ),
              subtitle: t(
                "profilePage.respiratorySignalChartSubtitle",
                "Detrended respiratory waveform from the recording.",
              ),
              points: detrendedSignal.points,
              stroke: "#107A92",
              fullWidth: true,
              emptyLabel: t(
                "profilePage.noRespiratorySignal",
                "No respiratory signal is available for this recording.",
              ),
            },
          ]
        : []),
      ...(showCardiacPoincare
        ? [
            {
              type: "poincare",
              featureKey: "poincareChart",
              title: t("result.pulse.poincare", "Poincaré"),
              x: cardiacPoincare.x,
              y: cardiacPoincare.y,
              fullWidth: isPulseKind,
              emptyLabel: t(
                "result.pulse.noPoincare",
                "No Poincaré data available.",
              ),
            },
          ]
        : []),
    ],
  };
}

function buildViewerOnlyPresentation(entry, data, t) {
  const kind = classifyRecording(entry, data);
  const titleKeyByKind = {
    spirometry: "profilePage.recordingKinds.spirometry",
    "post-exertion": "profilePage.recordingKinds.postExertion",
    "manual-audio": "profilePage.recordingKinds.manualAudio",
    pulse: "profilePage.recordingKinds.pulse",
    cardiopulmonary: "profilePage.recordingKinds.cardiopulmonary",
  };
  const fallbackTitleByKind = {
    spirometry: "Spirometry",
    "post-exertion": "Post-exertion",
    "manual-audio": "Manual audio",
    pulse: "Pulse",
    cardiopulmonary: "Cardiopulmonary",
  };
  const showPendingCardiacMetrics =
    kind === "cardiopulmonary" || kind === "post-exertion" || kind === "pulse";
  const pendingCardiopulmonaryMetrics = showPendingCardiacMetrics
    ? [
          {
            key: "hr",
            featureKey: "hr",
            label: "HR",
            value: formatValidatedMeasurement("hr", null),
            rawValue: null,
            unit: "bpm",
            trendDecimals: 0,
          },
          {
            key: "hrv",
            featureKey: "hrv",
            label: "HRV",
            value: formatValidatedMeasurement("hrv", null),
            rawValue: null,
            unit: "ms",
            trendDecimals: 0,
          },
          ...(kind === "pulse"
            ? []
            : [
                {
                  key: "rr",
                  featureKey: "rr",
                  label: "RR",
                  value: formatValidatedMeasurement("rr", null),
                  rawValue: null,
                  unit: "bpm",
                  trendDecimals: 0,
                },
                ...(kind === "post-exertion"
                  ? [
                      {
                        key: "rrMin",
                        featureKey: "rr",
                        label: t("profilePage.rrMin", "RR Min"),
                        value: formatOverlayMetric(
                          formatValidatedMeasurement("rr", null, "--"),
                        ),
                        rawValue: null,
                        unit: "bpm",
                        trendDecimals: 0,
                      },
                      {
                        key: "rrMax",
                        featureKey: "rr",
                        label: t("profilePage.rrMax", "RR Max"),
                        value: formatOverlayMetric(
                          formatValidatedMeasurement("rr", null, "--"),
                        ),
                        rawValue: null,
                        unit: "bpm",
                        trendDecimals: 0,
                      },
                    ]
                  : []),
              ]),
        ]
      : [];
  const pendingCardiopulmonaryStatuses = showPendingCardiacMetrics
    ? [
          {
            key: "cardiacQuality",
            featureKey: "cardiacQuality",
            label: t("profilePage.cardiacQuality", "Cardiac quality"),
            value: formatStatusValue(null, t),
            rawValue: null,
            trendType: "quality",
          },
          ...(kind === "pulse"
            ? []
            : [
                {
                  key: "respiratoryQuality",
                  featureKey: "signalQuality",
                  label: t(
                    "profilePage.respiratoryQuality",
                    "Respiratory quality",
                  ),
                  value: formatStatusValue(null, t),
                  rawValue: null,
                  trendType: "quality",
                },
              ]),
        ]
      : [];

  return {
    kind,
    title: t(
      titleKeyByKind[kind] || "profilePage.recordingDetailsTitle",
      fallbackTitleByKind[kind] || "Recording details",
    ),
    metrics: pendingCardiopulmonaryMetrics,
    statuses: pendingCardiopulmonaryStatuses,
    trendMetrics: [],
    charts: [],
    averagedFlowLoopInline: false,
  };
}

function SummaryCard({
  label,
  value,
  unit,
  disabled = false,
  valueClassName = "text-typography",
}) {
  return (
    <div className="card h-full px-3 py-2">
      <p className="text-sm text-greyscale-3">{label}</p>
      <div className="flex items-end gap-2">
        <p
          className={`text-2xl font-semibold ${
            disabled ? "text-greyscale-3" : valueClassName
          }`}
        >
          {value}
        </p>
        {unit ? <p className="pb-1 text-sm text-greyscale-3">{unit}</p> : null}
      </div>
    </div>
  );
}

// Fill mode (spirometry segment): the chart grows to fill its grid-stretched
// card. Clamp the measured height so a degenerate layout can never grow the
// canvas unbounded, and fall back to the min when no height is measurable (e.g.
// jsdom/tests) so Chart.js always sizes against a stable, bounded box.
const FILL_MIN_CHART_HEIGHT = 224;
const FILL_MAX_CHART_HEIGHT = 560;

function TimeSeriesChart({
  title,
  points,
  emptyLabel,
  stroke,
  series,
  fill = false,
}) {
  const seriesList = useMemo(
    () =>
      Array.isArray(series) && series.length
        ? series
        : [
            {
              label: title,
              points,
              stroke,
            },
          ],
    [points, series, stroke, title],
  );

  const normalisedSeries = useMemo(
    () =>
      seriesList
        .map((seriesItem) => ({
          ...seriesItem,
          points: normaliseTimeValuePoints(seriesItem?.points || []),
        }))
        .filter((seriesItem) => seriesItem.points.length),
    [seriesList],
  );

  // Fill mode: measure the (flex-sized) chart area and pass Chart.js a concrete,
  // clamped pixel height, rather than a percentage — this keeps SignalChart on
  // its stable fixed-height path so Chart.js's responsive observer can't churn
  // or grow the canvas unbounded. Falls back to the min when unmeasurable.
  const chartAreaRef = useRef(null);
  const [fillHeight, setFillHeight] = useState(FILL_MIN_CHART_HEIGHT);
  useEffect(() => {
    if (!fill) return undefined;
    const el = chartAreaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect?.height;
      if (typeof measured === "number" && measured > 0) {
        setFillHeight(
          Math.min(
            Math.max(Math.round(measured), FILL_MIN_CHART_HEIGHT),
            FILL_MAX_CHART_HEIGHT,
          ),
        );
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fill]);

  if (!normalisedSeries.length) {
    return (
      <section className="h-full rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>

        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      </section>
    );
  }

  const maxSecs = normalisedSeries.reduce((maxValue, seriesItem) => {
    return Math.max(maxValue, getPointSeriesMaxSecs(seriesItem.points, 0));
  }, 0);
  const rawSeries = normalisedSeries.length > 1 ? normalisedSeries[0] : null;
  const smoothSeries =
    normalisedSeries.length > 1 ? normalisedSeries[1] : normalisedSeries[0];
  const displaySeries = rawSeries ? [rawSeries, smoothSeries] : [smoothSeries];

  return (
    <section
      className={`h-full rounded-xl border border-greyscale-1 bg-container p-4 ${
        fill ? "flex flex-col" : ""
      }`}
    >
      <h3 className="text-lg font-semibold text-typography">{title}</h3>

      {displaySeries.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-greyscale-3">
          {displaySeries.map((seriesItem) => (
            <div key={seriesItem.label} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: seriesItem.stroke }}
              />
              <span>{seriesItem.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      {/* When `fill`, this flex-1 area grows to the remaining card height (the
          card's height is definite — the grid row is stretched by a taller
          sibling). min-h-0 lets it shrink; its measured height is passed to the
          chart as a concrete, clamped pixel value (see the ResizeObserver). */}
      <div
        ref={chartAreaRef}
        className={fill ? "mt-3 min-h-0 flex-1" : ""}
      >
        <SignalChart
          rawSignal={rawSeries?.points || []}
          smoothSignal={smoothSeries?.points || []}
          showRawSignal={Boolean(rawSeries)}
          rawBorderColor={rawSeries?.stroke}
          smoothBorderColor={smoothSeries?.stroke}
          peaks={[]}
          troughs={[]}
          maxSecs={Math.max(1, Math.ceil(maxSecs || 0))}
          height={fill ? fillHeight : 224}
          tickStepSize={maxSecs <= 10 ? 0.5 : 5}
        />
      </div>
    </section>
  );
}

function PoincareChartCard({
  title,
  x = [],
  y = [],
  emptyLabel,
  axisMin,
  axisMax,
  xTitle,
  yTitle,
}) {
  const hasData = Array.isArray(x) && Array.isArray(y) && x.length && y.length;

  return (
    <section className="h-full rounded-xl border border-greyscale-1 bg-container p-4">
      <h3 className="text-lg font-semibold text-typography">{title}</h3>

      {hasData ? (
        <PoincareChart
          x={x}
          y={y}
          height={280}
          initialMode="basic"
          axisMin={axisMin}
          axisMax={axisMax}
          xTitle={xTitle}
          yTitle={yTitle}
        />
      ) : (
        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      )}
    </section>
  );
}

function getAveragedFlowLoopSectionTitle(entry, data, t) {
  const averagedFlowLoop = pickAveragedFlowLoop(data);

  if (!averagedFlowLoop) {
    return null;
  }

  if (classifyRecording(entry, data) === "post-exertion") {
    return t(
      "results.flowLoops.rollingRecoverySectionTitle",
      "Rolling recovery flow-volume loops",
    );
  }

  return t(
    averagedFlowLoop.mode === "windowed"
      ? "results.flowLoops.averagedSectionTitlePlural"
      : "results.flowLoops.averagedSectionTitle",
    averagedFlowLoop.mode === "windowed"
      ? "Average Flow-Volume Loops"
      : "Average Flow-Volume Loop",
  );
}

export default function PatientRecordingOverlay({
  entry,
  open,
  onClose,
  dashboardResultFeatures = null,
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useDateTimeFormat();
  const environmentalDebugKeyRef = useRef("");
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: null,
    detail: null,
    variant: null,
    pairedData: null,
    pairedDetail: null,
    pairedVariant: null,
    pairedError: null,
  });

  useEffect(() => {
    if (!open || !entry?.uid || !entry?.name) {
      setState({
        loading: false,
        error: null,
        data: null,
        detail: null,
        variant: null,
        pairedData: null,
        pairedDetail: null,
        pairedVariant: null,
        pairedError: null,
      });
      return;
    }

    let cancelled = false;

    const loadRecording = async () => {
      setState({
        loading: true,
        error: null,
        data: null,
        detail: null,
        variant: null,
        pairedData: null,
        pairedDetail: null,
        pairedVariant: null,
        pairedError: null,
      });

      try {
        const match = await loadPatientRecordingDocument(entry);
        if (cancelled) return;
        const pairedEntry = resolveSixMinuteWalkPairedEntry(entry, match.data);
        let pairedMatch = null;
        let pairedError = null;

        if (pairedEntry) {
          try {
            pairedMatch = await loadPatientRecordingDocument(pairedEntry, {
              variant:
                pairedEntry?.collectionName ||
                pairedEntry?.variant ||
                match.variant ||
                null,
            });
          } catch (error) {
            pairedError = error;
          }
        }

        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          data: match.data,
          detail: match.detail,
          variant: match.variant,
          pairedData: pairedMatch?.data ?? null,
          pairedDetail: pairedMatch?.detail ?? null,
          pairedVariant: pairedMatch?.variant ?? null,
          pairedError,
        });
      } catch (error) {
        if (cancelled) return;
        setState({
          loading: false,
          error,
          data: null,
          detail: null,
          variant: null,
          pairedData: null,
          pairedDetail: null,
          pairedVariant: null,
          pairedError: null,
        });
      }
    };

    loadRecording();

    return () => {
      cancelled = true;
    };
  }, [entry, open]);

  const recordingTime = useMemo(
    () => formatDateTime(entry?.takenAt),
    [entry?.takenAt, formatDateTime],
  );

  const recordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(entry?.docId || entry?.name),
    [entry?.docId, entry?.name],
  );
  const showRecordingViewer = isDashboardResultFeatureVisible(
    dashboardResultFeatures,
    "videoAudioReview",
  );

  const presentation = useMemo(() => {
    if (!state.data || !entry) return null;
    const entryWithVariant = { ...entry, variant: state.variant };
    const hasReviewMedia = Boolean(state.data?.reviewMedia?.url);
    const hasDerivedResults = Boolean(
      state.data?.results ||
        state.data?.["mpm-signal-processed"] ||
        state.data?.["pca-signal-processed"],
    );

    if (showRecordingViewer && hasReviewMedia && !hasDerivedResults) {
      return buildViewerOnlyPresentation(entryWithVariant, state.data, t);
    }

    return filterPatientRecordingPresentation(
      buildRecordingPresentation(entryWithVariant, state.data, t),
      dashboardResultFeatures,
    );
  }, [dashboardResultFeatures, entry, showRecordingViewer, state.data, state.variant, t]);

  const showSpectrogramPanel =
    showRecordingViewer &&
    isDashboardResultFeatureVisible(dashboardResultFeatures, "spectrograms");
  const showAveragedFlowLoopSection = useMemo(() => {
    if (!presentation?.kind) {
      return false;
    }

    if (
      presentation.kind === "six-minute-walk" ||
      presentation.kind === "pulse"
    ) {
      return false;
    }

    if (presentation.kind === "post-exertion") {
      return isDashboardResultFeatureVisible(
        dashboardResultFeatures,
        "rollingRecoveryFlowVolumeLoops",
      );
    }

    return isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "averageFlowVolumeLoopChart",
    );
  }, [dashboardResultFeatures, presentation?.kind]);
  const showFlowVolumeLoopsSection = useMemo(() => {
    if (!presentation?.kind) {
      return false;
    }

    if (
      presentation.kind === "six-minute-walk" ||
      presentation.kind === "pulse"
    ) {
      return false;
    }

    return isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "flowVolumeLoopsCharts",
    );
  }, [dashboardResultFeatures, presentation?.kind]);
  const showEnvironmentalContextSection =
    isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "environmentalContext",
    ) &&
    isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "weatherDetails",
    );

  const averagedFlowLoopSectionTitle = useMemo(() => {
    if (!state.data || !entry) {
      return null;
    }

    return getAveragedFlowLoopSectionTitle(
      { ...entry, variant: state.variant },
      state.data,
      t,
    );
  }, [entry, state.data, state.variant, t]);

  const showSixMinuteWalkAssessment = presentation?.kind === "six-minute-walk";
  const showSixMinuteWalkPreAveragedFlowLoop =
    showSixMinuteWalkAssessment &&
    isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "averageFlowVolumeLoopChart",
    );
  const showSixMinuteWalkPostRollingFlowLoops =
    showSixMinuteWalkAssessment &&
    isDashboardResultFeatureVisible(
      dashboardResultFeatures,
      "rollingRecoveryFlowVolumeLoops",
    );
  const overlayEnvironmentalContext =
    !showSixMinuteWalkAssessment && showEnvironmentalContextSection
      ? state.data?.environmentalContext || null
      : null;
  const overlayEnvironmentalPending =
    !showSixMinuteWalkAssessment &&
    showEnvironmentalContextSection &&
    isWithinPendingWindow(recordingTimestamp);
  const showOverlayEnvironmentalCards =
    Boolean(overlayEnvironmentalContext) || overlayEnvironmentalPending;
  const overlayEnvironmentalSnapshot = useMemo(
    () => getEnvironmentalContextSnapshot(overlayEnvironmentalContext),
    [overlayEnvironmentalContext],
  );
  const overlayHeaderLabel =
    overlayEnvironmentalSnapshot?.locationDisplayName || state.variant;

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || process.env.NODE_ENV === "test") {
      return;
    }

    if (!open || !entry) {
      environmentalDebugKeyRef.current = "";
      return;
    }

    const observationVariables = Array.isArray(overlayEnvironmentalContext?.observations)
      ? overlayEnvironmentalContext.observations
          .map((entryItem) => String(entryItem?.variable || "").trim())
          .filter(Boolean)
      : [];
    const pollutantVariables = Array.isArray(overlayEnvironmentalSnapshot?.pollutants)
      ? overlayEnvironmentalSnapshot.pollutants
          .map((entryItem) => String(entryItem?.variable || "").trim())
          .filter(Boolean)
      : [];
    const renderedMetricKeys = [
      overlayEnvironmentalSnapshot?.metrics?.aqi ? "aqi" : null,
      overlayEnvironmentalSnapshot?.metrics?.temperature ? "temperature" : null,
      overlayEnvironmentalSnapshot?.metrics?.humidity ? "humidity" : null,
      overlayEnvironmentalSnapshot?.metrics?.pollen ? "pollen" : null,
      overlayEnvironmentalSnapshot?.metrics?.pressure ? "pressure" : null,
      ...pollutantVariables.map((variable) => `pollutant:${variable}`),
    ].filter(Boolean);
    const debugPayload = {
      uid: entry?.uid || null,
      docId: entry?.docId || entry?.name || null,
      variant: state.variant || entry?.collectionName || entry?.variant || null,
      location: overlayEnvironmentalContext?.location || null,
      observationVariables,
      pollutantVariables,
      renderedMetricKeys,
      pressureObservation: overlayEnvironmentalSnapshot?.metrics?.pressure || null,
      limitations: Array.isArray(overlayEnvironmentalContext?.limitations)
        ? overlayEnvironmentalContext.limitations
        : [],
      errors: Array.isArray(overlayEnvironmentalContext?.errors)
        ? overlayEnvironmentalContext.errors
        : [],
    };
    const debugKey = JSON.stringify(debugPayload);

    if (debugKey === environmentalDebugKeyRef.current) {
      return;
    }

    environmentalDebugKeyRef.current = debugKey;
    console.info("[PatientRecordingOverlay] environmental_context_debug", debugPayload);

    if (overlayEnvironmentalContext && pollutantVariables.length === 0) {
      console.warn("[PatientRecordingOverlay] environmental_context_pollutants_missing", {
        uid: debugPayload.uid,
        docId: debugPayload.docId,
        variant: debugPayload.variant,
        observationVariables,
        errors: debugPayload.errors,
      });
    }
  }, [
    entry,
    open,
    overlayEnvironmentalContext,
    overlayEnvironmentalSnapshot,
    state.variant,
  ]);

  if (!open || !entry) {
    return null;
  }

  // The averaged flow-loop card. Spirometry renders it inside the charts grid
  // (beside the spirometry segment); other kinds render it as its own full-width
  // section below the charts. Same markup either way.
  const averagedFlowLoopCard =
    showAveragedFlowLoopSection && averagedFlowLoopSectionTitle ? (
      <section className="rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">
          {averagedFlowLoopSectionTitle}
        </h3>

        <div className="mt-4">
          <AveragedFlowLoopsSection flowLoopSource={state.data} />
        </div>
      </section>
    ) : null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t("profilePage.closeOverlay", "Close recording details")}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-greyscale-1 bg-background shadow-2xl">
        <div className="flex items-start justify-between gap-4 p-4 pb-2">
          <div className="min-w-0 w-full">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold text-typography">
                {presentation?.title ||
                  t("profilePage.recordingDetailsTitle", "Recording details")}
              </h2>
              {overlayHeaderLabel ? (
                <span className="rounded-full bg-greyscale-1 px-3 py-1 text-xs font-medium text-typography">
                  {overlayHeaderLabel}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-greyscale-3">
              {recordingTime.date || "--"} {recordingTime.time || "--"}
            </p>
          </div>

          <button
            type="button"
            className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
            onClick={onClose}
          >
            {t("profilePage.closeOverlayButton", "Close")}
          </button>
        </div>

        <div className="flex-1 justify-start overflow-y-auto px-4 pt-2 pb-4">
          {state.loading ? (
            <div className="flex h-full items-center justify-center">
              <p>{t("profilePage.loadingRecording", "Loading recording…")}</p>
            </div>
          ) : state.error ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-red-500">
                {t(
                  "profilePage.recordingLoadError",
                  "We couldn't load that recording. Please try again.",
                )}
              </p>
            </div>
          ) : !presentation ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-greyscale-3">
                {t(
                  "profilePage.recordingUnavailable",
                  "No preview data is available for this recording.",
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col justify-start gap-4">
              {showSixMinuteWalkAssessment ? (
                <SixMinuteWalkAssessmentSection
                  preData={state.pairedData}
                  postData={state.data}
                  showPreAveragedFlowLoop={showSixMinuteWalkPreAveragedFlowLoop}
                  showPostRollingFlowLoops={showSixMinuteWalkPostRollingFlowLoops}
                  t={t}
                />
              ) : null}

              {!showSixMinuteWalkAssessment &&
              (presentation.metrics?.length || presentation.statuses?.length) ? (
                <section className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
                  {presentation.statuses?.length
                    ? presentation.statuses.map((status) => {
                        const hasValue =
                          status.trendType === "quality"
                            ? hasResolvedQualityStatus(status.rawValue)
                            : hasMetricValue(status.rawValue);
                        const pending =
                          !hasValue &&
                          isWithinPendingWindow(recordingTimestamp);
                        return (
                          <SummaryCard
                            key={status.label}
                            label={status.label}
                            value={pending ? renderCalculatingValue(t) : status.value}
                            unit={pending ? undefined : status.unit}
                          />
                        );
                      })
                    : null}

                  {presentation.metrics?.length
                    ? presentation.metrics.map((metric) => {
                        // No value yet + recording is recent -> still calculating.
                        // Quality metrics carry a non-numeric status, so only a
                        // resolved pass/fail/good/bad value counts as "ready";
                        // numeric metrics use the finite-number check.
                        const hasValue =
                          metric.trendType === "quality"
                            ? hasResolvedQualityStatus(metric.rawValue)
                            : hasMetricValue(metric.rawValue);
                        const pending =
                          !hasValue &&
                          isWithinPendingWindow(recordingTimestamp);
                        return (
                          <SummaryCard
                            key={metric.label}
                            label={metric.label}
                            value={pending ? renderCalculatingValue(t) : metric.value}
                            unit={pending ? undefined : metric.unit}
                            disabled={metric.disabled}
                          />
                        );
                      })
                    : null}
                </section>
              ) : null}

              {!showSixMinuteWalkAssessment &&
              (presentation.charts?.length ||
              (presentation.averagedFlowLoopInline && averagedFlowLoopCard)) ? (
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  {presentation.charts?.map((chart) => (
                    <div
                      key={chart.title}
                      className={chart.fullWidth ? "xl:col-span-2" : ""}
                    >
                      {chart.type === "poincare" ? (
                        <PoincareChartCard
                          title={chart.title}
                          x={chart.x}
                          y={chart.y}
                          axisMin={chart.axisMin}
                          axisMax={chart.axisMax}
                          xTitle={chart.xTitle}
                          yTitle={chart.yTitle}
                          emptyLabel={chart.emptyLabel}
                        />
                      ) : (
                        <TimeSeriesChart
                          title={chart.title}
                          points={chart.points}
                          series={chart.series}
                          stroke={chart.stroke}
                          emptyLabel={chart.emptyLabel}
                          fill={chart.fill}
                        />
                      )}
                    </div>
                  ))}
                  {presentation.averagedFlowLoopInline ? averagedFlowLoopCard : null}
                </div>
              ) : null}

              {!showSixMinuteWalkAssessment && !presentation.averagedFlowLoopInline
                ? averagedFlowLoopCard
                : null}

              {!showSixMinuteWalkAssessment && showFlowVolumeLoopsSection ? (
                <FlowVolumeLoopsSection recordJson={state.data} />
              ) : null}

              {showOverlayEnvironmentalCards ? (
                <section className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
                  <EnvironmentalContextSection
                    context={overlayEnvironmentalContext}
                    t={t}
                    CardComponent={SummaryCard}
                    showPendingPlaceholders={overlayEnvironmentalPending}
                  />
                </section>
              ) : null}

              {showRecordingViewer ? (
                <RecordingViewerSection
                  title={t(
                    "profilePage.recordingViewerTitle",
                    "Recording viewer",
                  )}
                  variant={
                    state.variant ||
                    state.detail?.variant ||
                    entry?.collectionName ||
                    entry?.variant
                  }
                  measurement={state.data}
                  diagnosticContext={{
                    uid: entry?.uid || state.detail?.uid || null,
                    docId: entry?.docId || entry?.name || null,
                  }}
                  emptyLabel={t(
                    "profilePage.recordingViewerUnavailable",
                    "No reviewable media is available for this recording.",
                  )}
                  loadingLabel={t(
                    "profilePage.recordingViewerLoading",
                    "Loading recording media…",
                  )}
                  errorLabel={t(
                    "profilePage.recordingViewerLoadError",
                    "We couldn't load the recording media for this entry.",
                  )}
                  videoTitle={t(
                    "profilePage.recordingViewerVideoTitle",
                    "Video review",
                  )}
                  audioTitle={t(
                    "profilePage.recordingViewerAudioTitle",
                    "Audio review",
                  )}
                  audioSpectrumLoading={t(
                    "profilePage.recordingViewerSpectrumLoading",
                    "Preparing the full audio spectrum for review…",
                  )}
                  showSpectrogramPanel={showSpectrogramPanel}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
